import React from 'react'

function getTips(loans, emiPayments) {
  const tips = []

  if (!loans.length) {
    return [{
      icon: '📋', title: 'No loans tracked yet',
      desc: 'Add your loans to get personalized credit score tips.',
      impact: 'info', color: '#8a8278'
    }]
  }

  // Utilization: avg balance / original across all loans
  const loansWithOriginal = loans.filter(l => parseFloat(l.original_amount) > 0)
  if (loansWithOriginal.length > 0) {
    const avgUtil = loansWithOriginal.reduce((s, l) => s + parseFloat(l.balance) / parseFloat(l.original_amount), 0) / loansWithOriginal.length
    if (avgUtil > 0.8) {
      tips.push({ icon: '⚠️', title: 'High Credit Utilization', impact: 'high', color: '#b8441a', desc: `Your average loan utilization is ${Math.round(avgUtil * 100)}%. Keeping balances below 30% of original amount significantly boosts your score. Focus on paying down ${loansWithOriginal.sort((a, b) => parseFloat(b.balance) / parseFloat(b.original_amount) - parseFloat(a.balance) / parseFloat(a.original_amount))[0].name} first.` })
    } else if (avgUtil > 0.5) {
      tips.push({ icon: '📉', title: 'Moderate Utilization', impact: 'medium', color: '#c9a84c', desc: `Utilization at ${Math.round(avgUtil * 100)}%. Aim to reduce it below 30% for a better score. Making extra payments helps.` })
    } else {
      tips.push({ icon: '✅', title: 'Good Utilization Ratio', impact: 'positive', color: '#4a6741', desc: `Your loan utilization is ${Math.round(avgUtil * 100)}% — well under 50%. This positively impacts your credit score.` })
    }
  }

  // Payment history — check last 3 months
  const now = new Date()
  const last3 = []
  for (let i = 0; i < 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    last3.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  const recentPayments = emiPayments.filter(p => last3.includes(p.payment_month))
  const unpaidCount = recentPayments.filter(p => p.status === 'unpaid').length
  const paidCount = recentPayments.filter(p => p.status === 'paid').length
  if (recentPayments.length > 0) {
    if (unpaidCount > 0) {
      tips.push({ icon: '🚨', title: 'Missed Payments Detected', impact: 'high', color: '#b8441a', desc: `${unpaidCount} unpaid EMI${unpaidCount > 1 ? 's' : ''} in the last 3 months. Payment history is the single biggest factor in your credit score (35%). Set up auto-pay to avoid missed payments.` })
    } else if (paidCount > 0) {
      tips.push({ icon: '✅', title: 'Excellent Payment History', impact: 'positive', color: '#4a6741', desc: `All tracked EMIs are paid on time recently. Consistent on-time payments are the #1 factor in building a strong credit score. Keep it up!` })
    }
  }

  // High interest → refinancing
  const highRateLoans = loans.filter(l => parseFloat(l.interest_rate) > 14)
  if (highRateLoans.length > 0) {
    tips.push({ icon: '💡', title: 'Refinancing Opportunity', impact: 'medium', color: '#c9a84c', desc: `${highRateLoans.map(l => l.name).join(', ')} ${highRateLoans.length === 1 ? 'has' : 'have'} a rate above 14%. Consider refinancing or balance transfer to lower rates — this reduces your interest burden and frees cash for faster payoff.` })
  }

  // Loan count
  if (loans.length >= 5) {
    tips.push({ icon: '📦', title: 'Multiple Active Loans', impact: 'medium', color: '#c9a84c', desc: `You have ${loans.length} active loans. Lenders view many simultaneous loans as a risk. Consider consolidating smaller debts into one personal loan to simplify repayment and reduce inquiries.` })
  }

  // Credit mix — positive if diverse types
  const types = [...new Set(loans.map(l => l.type))]
  if (types.length >= 2) {
    tips.push({ icon: '🧩', title: 'Good Credit Mix', impact: 'positive', color: '#4a6741', desc: `You have ${types.length} types of credit (${types.map(t => ({ mortgage: 'Home', auto: 'Auto', student: 'Education', personal: 'Personal', credit: 'Credit Card' })[t] || t).join(', ')}). A diverse mix of credit types adds a small positive factor to your score.` })
  }

  // Payoff strategy
  const byRate = [...loans].sort((a, b) => parseFloat(b.interest_rate) - parseFloat(a.interest_rate))
  if (byRate.length >= 2) {
    tips.push({ icon: '🎯', title: 'Use the Avalanche Method', impact: 'tip', color: '#3d4a5c', desc: `Pay minimums on all loans, then put any extra cash toward "${byRate[0].name}" (highest rate at ${byRate[0].interest_rate}%). This minimizes total interest paid. Once cleared, attack "${byRate[1].name}" next.` })
  }

  // Emergency fund
  const totalMonthlyEmi = loans.reduce((s, l) => s + parseFloat(l.emi_amount || 0), 0)
  if (totalMonthlyEmi > 0) {
    tips.push({ icon: '🛡️', title: 'Maintain an Emergency Fund', impact: 'tip', color: '#3d4a5c', desc: `Your monthly EMI total is ₹${Math.round(totalMonthlyEmi).toLocaleString('en-IN')}. Keep at least 3-6 months of EMIs (₹${Math.round(totalMonthlyEmi * 3).toLocaleString('en-IN')}–₹${Math.round(totalMonthlyEmi * 6).toLocaleString('en-IN')}) as an emergency fund so unexpected events don't cause missed payments.` })
  }

  // Debt-free encouragement
  const loansWithEmi = loans.filter(l => parseFloat(l.emi_amount) > 0)
  if (loansWithEmi.length > 0) {
    const soonest = loansWithEmi.sort((a, b) => {
      const mA = Math.ceil(parseFloat(a.balance) / parseFloat(a.emi_amount))
      const mB = Math.ceil(parseFloat(b.balance) / parseFloat(b.emi_amount))
      return mA - mB
    })[0]
    const months = Math.ceil(parseFloat(soonest.balance) / parseFloat(soonest.emi_amount))
    if (months <= 12) {
      tips.push({ icon: '🏁', title: `Almost Debt-Free on ${soonest.name}!`, impact: 'positive', color: '#4a6741', desc: `Only ${months} month${months !== 1 ? 's' : ''} left on "${soonest.name}". Once cleared, redirect that EMI toward the next debt — this snowball effect dramatically accelerates your debt-free date.` })
    }
  }

  return tips
}

const impactLabel = { high: 'HIGH IMPACT', medium: 'MEDIUM', positive: 'POSITIVE', tip: 'TIP', info: 'INFO' }
const impactBg = { high: 'rgba(184,68,26,0.08)', medium: 'rgba(201,168,76,0.1)', positive: 'rgba(74,103,65,0.08)', tip: 'rgba(61,74,92,0.08)', info: 'rgba(138,130,120,0.08)' }
const impactBorder = { high: 'rgba(184,68,26,0.2)', medium: 'rgba(201,168,76,0.25)', positive: 'rgba(74,103,65,0.2)', tip: 'rgba(61,74,92,0.15)', info: 'rgba(138,130,120,0.15)' }

function scoreEstimate(loans, emiPayments) {
  if (!loans.length) return { score: null, label: 'No data', color: '#8a8278' }
  let score = 700
  const loansWithOriginal = loans.filter(l => parseFloat(l.original_amount) > 0)
  if (loansWithOriginal.length) {
    const avgUtil = loansWithOriginal.reduce((s, l) => s + parseFloat(l.balance) / parseFloat(l.original_amount), 0) / loansWithOriginal.length
    if (avgUtil > 0.8) score -= 60
    else if (avgUtil > 0.5) score -= 25
    else score += 20
  }
  const now = new Date()
  const last3 = []
  for (let i = 0; i < 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    last3.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  const recent = emiPayments.filter(p => last3.includes(p.payment_month))
  const unpaid = recent.filter(p => p.status === 'unpaid').length
  if (unpaid > 0) score -= unpaid * 40
  const paid = recent.filter(p => p.status === 'paid').length
  if (paid > 0) score += Math.min(paid * 8, 40)
  if (loans.length >= 5) score -= 20
  const types = [...new Set(loans.map(l => l.type))]
  if (types.length >= 3) score += 15
  score = Math.max(300, Math.min(900, score))
  const label = score >= 750 ? 'Excellent' : score >= 700 ? 'Good' : score >= 650 ? 'Fair' : score >= 600 ? 'Poor' : 'Very Poor'
  const color = score >= 750 ? '#4a6741' : score >= 700 ? '#c9a84c' : score >= 650 ? '#c9a84c' : '#b8441a'
  return { score, label, color }
}

export default function CreditScoreTips({ loans, emiPayments }) {
  const tips = getTips(loans, emiPayments)
  const { score, label, color } = scoreEstimate(loans, emiPayments)

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* Score card */}
      {score && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Estimated Credit Health</span>
          </div>
          <div style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: '3.5rem', fontWeight: '900', color, lineHeight: 1 }}>{score}</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '0.25rem' }}>{label}</div>
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ height: '10px', background: 'var(--surface-hdr)', borderRadius: '99px', overflow: 'hidden', marginBottom: '0.5rem', position: 'relative' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, #b8441a, #c9a84c, #4a6741)', borderRadius: '99px' }} />
                <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, background: 'var(--surface-hdr)', borderRadius: '99px', width: `${100 - ((score - 300) / 600 * 100)}%` }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)' }}>
                <span>300</span><span>500</span><span>700</span><span>900</span>
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                * Estimate based on your tracked loan behavior. Check CIBIL/Experian for your official score.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tips */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem' }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Personalized Tips</span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', marginLeft: '0.75rem' }}>{tips.length} insights</span>
        </div>
        <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {tips.map((tip, i) => (
            <div key={i} style={{ background: impactBg[tip.impact], border: `1px solid ${impactBorder[tip.impact]}`, borderRadius: '8px', padding: '1rem 1.2rem', borderLeft: `4px solid ${tip.color}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.4rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1rem' }}>{tip.icon}</span>
                  <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.92rem', fontWeight: '700', color: 'var(--text)' }}>{tip.title}</span>
                </div>
                <span style={{ fontFamily: 'monospace', fontSize: '0.55rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: tip.color, background: impactBorder[tip.impact], padding: '0.15rem 0.4rem', borderRadius: '3px', flexShrink: 0 }}>
                  {impactLabel[tip.impact]}
                </span>
              </div>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>{tip.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
